import { Box, Link, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { JSX, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { XOP_CONTACT, XOP_TEL_HREF } from "../../lib/company.js";
import type { SiteMapColumn } from "../public/content.js";
import { MailIcon, MapPinIcon, PhoneIcon } from "./icons.js";

const colLink = {
  display: "block",
  fontSize: 13,
  lineHeight: 1.9,
  color: "text.secondary",
  textDecoration: "none",
  "&:hover": { color: "text.primary" },
} as const;

function FooterColumn({ heading, children }: { heading: string; children: ReactNode }): JSX.Element {
  return (
    <Box>
      <Typography component="h2" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "text.primary", mb: 1 }}>
        {heading}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * Branded footer strip — the customer support portal's auth footer, in MUI:
 * who we are on the left, how to reach us in the middle, site + copyright on
 * the right, over a hairline rule.
 *
 * Colour comes from the theme (`text.primary` / `text.secondary` /
 * `divider`), not from fixed white alphas: the strip sits on the hero, and
 * the hero flips with the top bar's theme toggle — near-black type on the
 * light field, near-white on the dark one. That is what the reference does,
 * and hard-coding white here is exactly how a light mode ends up with an
 * invisible footer.
 *
 * `siteMap` adds the public site's link columns above the strip; the wall
 * and the invitation page pass nothing. Those columns are page links only
 * (Company, Portal) — the contact details are printed once, in the strip;
 * a "Contact" column above it was the same phone number and the same two
 * mailboxes twice on one screen.
 */
export function SiteFooter({ siteMap }: { siteMap?: readonly SiteMapColumn[] } = {}): JSX.Element {
  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        zIndex: 2,
        mx: "auto",
        width: "100%",
        maxWidth: 1380,
        px: { xs: 2.5, sm: 5, lg: 7 },
        pb: { xs: 3, lg: 4 },
      }}
    >
      {siteMap ? (
        <Box
          component="nav"
          aria-label="Footer"
          sx={{
            display: "grid",
            gap: { xs: 3, md: 6 },
            // Two columns at every size: the site map is Company + Portal,
            // and the contact details live in the strip below rather than
            // in a third column that repeated them.
            gridTemplateColumns: "1fr 1fr",
            pb: 4,
            mb: 1,
            fontFamily: "var(--font-display)",
            "& *": { fontFamily: "var(--font-display)" },
          }}
        >
          {siteMap.map((col) => (
            <FooterColumn key={col.heading} heading={col.heading}>
              {col.links.map((l) =>
                l.external ? (
                  <Link key={l.href} href={l.href} target="_blank" rel="noreferrer" sx={colLink}>
                    {l.label}
                  </Link>
                ) : (
                  <Link key={l.href} component={RouterLink} to={l.href} sx={colLink}>
                    {l.label}
                  </Link>
                ),
              )}
            </FooterColumn>
          ))}
        </Box>
      ) : null}
      <Box sx={{ height: "1px", bgcolor: "divider" }} />
      <Box
        sx={{
          pt: 2,
          display: "grid",
          gap: { xs: 1.5, lg: 4 },
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0,4fr) minmax(0,5fr) minmax(0,3fr)" },
          alignItems: "start",
          // The reference footer is DM Sans, not Inter. Same 12px, same
          // weights — but Inter's larger x-height makes it read a size
          // bigger, which is why this block looked heavier than the
          // support portal's next to it. The descendant selector is
          // needed because MUI Typography stamps the theme family onto
          // every child, so setting it on this Box alone does nothing.
          fontFamily: "var(--font-display)",
          "& *": { fontFamily: "var(--font-display)" },
          fontSize: 12,
        }}
      >
        <Box>
          <Typography
            component="p"
            sx={{ fontFamily: "var(--font-display)", fontSize: 12, fontWeight: 600, color: "text.primary" }}
          >
            {XOP_CONTACT.company}
          </Typography>
          <Box
            sx={{
              mt: 0.75,
              display: "inline-flex",
              alignItems: "flex-start",
              gap: 0.75,
              fontSize: 12,
              lineHeight: 1.6,
              color: "text.secondary",
            }}
          >
            <MapPinIcon />
            <span>{XOP_CONTACT.addressLine}</span>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            columnGap: 3,
            rowGap: 1,
            fontSize: 12,
            color: "text.secondary",
          }}
        >
          <FooterLink href={XOP_TEL_HREF} icon={<PhoneIcon />} label={XOP_CONTACT.phone} />
          <FooterLink
            href={`mailto:${XOP_CONTACT.support}`}
            icon={<MailIcon />}
            label={XOP_CONTACT.support}
          />
          <FooterLink
            href={`mailto:${XOP_CONTACT.sales}`}
            icon={<MailIcon />}
            label={XOP_CONTACT.sales}
          />
        </Box>

        <Box sx={{ textAlign: { xs: "left", lg: "right" } }}>
          <Link
            href={`https://${XOP_CONTACT.website}`}
            target="_blank"
            rel="noreferrer"
            sx={{
              fontSize: 12,
              color: "text.secondary",
              "&:hover": { color: "text.primary" },
            }}
          >
            {XOP_CONTACT.website}
          </Link>
          <Typography
            component="p"
            sx={{ fontFamily: "var(--font-display)", mt: 0.5, fontSize: 11.5, color: (t) => alpha(t.palette.text.secondary, 0.75) }}
          >
            © {new Date().getFullYear()} {XOP_CONTACT.company}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function FooterLink({
  href,
  icon,
  label,
}: {
  href: string;
  icon: JSX.Element;
  label: string;
}): JSX.Element {
  return (
    <Link
      href={href}
      underline="none"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        fontSize: 12,
        color: "inherit",
        transition: "color 150ms",
        "&:hover": { color: "text.primary" },
      }}
    >
      {icon}
      {label}
    </Link>
  );
}
